/**
 * Tool beams as direct streak instances (M2 plan §6 M2-20): each frame every active beam appends one streak to its
 * VFX pool (`pushInstance`, fx/particles.ts), from the muzzle to the contact point or the end of its range:
 * the mining beam amber-white and wider with laser power, the cutting beam white-cyan, the tractor a wide,
 * pale-blue, low-alpha streak to the held part. Ends come from the runtimes' readouts (float64 world, one tick
 * old); the muzzle end is moved by the player's interpolation offset so the beam leaves the drawn ship.
 * Allocation-free (D-014).
 */
import type { Vec3 } from '../../core/math'
import type { MiningReadout, SalvageReadout } from '../../space/contracts'
import { pushInstance, type ParticleSystem } from '../fx/particles'
import type { BeamConfig, M2FxConfig } from './config'

/** Streak shorter than this (m) is not drawn. */
const MIN_LENGTH = 1e-3

/** One beam streak in render space. */
export interface BeamStreak {
  /** Centre (render space). */
  cx: number
  cy: number
  cz: number
  length: number
  /** Unit direction from the muzzle to the end. */
  dx: number
  dy: number
  dz: number
}

export function createBeamStreak(): BeamStreak {
  return { cx: 0, cy: 0, cz: 0, length: 0, dx: 0, dy: 0, dz: 1 }
}

/**
 * The streak from float64 world `from` to `to` relative to the render origin `cam` into `out`. False when the
 * beam is degenerate (zero length).
 */
export function beamStreak(fx: number, fy: number, fz: number, tx: number, ty: number, tz: number, cam: Readonly<Vec3>, out: BeamStreak): boolean {
  const vx = tx - fx
  const vy = ty - fy
  const vz = tz - fz
  const len = Math.sqrt(vx * vx + vy * vy + vz * vz)
  if (len < MIN_LENGTH) return false
  out.length = len
  out.dx = vx / len
  out.dy = vy / len
  out.dz = vz / len
  out.cx = Math.fround(fx + vx * 0.5 - cam.x)
  out.cy = Math.fround(fy + vy * 0.5 - cam.y)
  out.cz = Math.fround(fz + vz * 0.5 - cam.z)
  return true
}

/** Streak width at laser power `power` 0..1 (a beam with equal ends has a fixed width). */
export function beamWidth(beam: Readonly<BeamConfig>, power: number): number {
  const p = power < 0 ? 0 : power > 1 ? 1 : power
  return beam.width[0] + (beam.width[1] - beam.width[0]) * p
}

/** Appends one beam streak to its pool. False when the pool is full. */
export function pushBeam(sys: ParticleSystem, beam: Readonly<BeamConfig>, streak: Readonly<BeamStreak>, width: number): boolean {
  return pushInstance(
    sys.pools[beam.material],
    streak.cx,
    streak.cy,
    streak.cz,
    beam.age,
    0.5,
    beam.tint[0],
    beam.tint[1],
    beam.tint[2],
    width,
    streak.length,
    streak.dx,
    streak.dy,
    streak.dz,
  )
}

const streak: BeamStreak = createBeamStreak()

/**
 * M10 P5a12c: booms 1–3 of a boom hull (`mining.booms`), each a chipping mining streak from its boom head to its
 * mark, rebased to the render origin `cam` like every beam (boom 0 is the mining beam above). Nothing on an S/M
 * hull. Returns the number of streaks drawn. Allocation-free.
 */
export function pushBoomBeams(
  sys: ParticleSystem,
  cfg: Readonly<M2FxConfig['beams']>,
  mining: Readonly<MiningReadout>,
  ox: number,
  oy: number,
  oz: number,
  cam: Readonly<Vec3>,
): number {
  const b = mining.booms
  if (b === undefined || b.count <= 1) return 0
  let n = 0
  for (let k = 1; k < b.count; k++) {
    if (b.on[k] !== 1) continue
    const i = k * 3
    if (beamStreak(b.from[i] + ox, b.from[i + 1] + oy, b.from[i + 2] + oz, b.to[i], b.to[i + 1], b.to[i + 2], cam, streak) && pushBeam(sys, cfg.mining, streak, beamWidth(cfg.mining, 0))) n++
  }
  return n
}

/**
 * Appends the streaks of every active beam for this frame: mining (power-driven width), cutting and tractor.
 * (ox, oy, oz) moves the muzzle end to the interpolated ship. Returns the number of streaks drawn.
 */
export function pushBeams(
  sys: ParticleSystem,
  cfg: Readonly<M2FxConfig['beams']>,
  mining: Readonly<MiningReadout>,
  salvage: Readonly<SalvageReadout>,
  ox: number,
  oy: number,
  oz: number,
  cam: Readonly<Vec3>,
): number {
  let n = 0
  if (mining.beamOn) {
    const f = mining.beamFrom
    const t = mining.beamTo
    if (beamStreak(f.x + ox, f.y + oy, f.z + oz, t.x, t.y, t.z, cam, streak) && pushBeam(sys, cfg.mining, streak, beamWidth(cfg.mining, mining.power))) n++
  }
  n += pushBoomBeams(sys, cfg, mining, ox, oy, oz, cam)
  if (salvage.beamOn) {
    const f = salvage.beamFrom
    const t = salvage.beamTo
    if (beamStreak(f.x + ox, f.y + oy, f.z + oz, t.x, t.y, t.z, cam, streak) && pushBeam(sys, cfg.cutting, streak, beamWidth(cfg.cutting, 1))) n++
  }
  if (salvage.tractorOn && salvage.tractorPart >= 0) {
    const f = salvage.beamFrom
    const t = salvage.tractorTo
    if (beamStreak(f.x + ox, f.y + oy, f.z + oz, t.x, t.y, t.z, cam, streak) && pushBeam(sys, cfg.tractor, streak, beamWidth(cfg.tractor, 1))) n++
  }
  return n
}
